import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { 
  FileSpreadsheet, 
  Download, 
  Loader2, 
  Calendar, 
  Users, 
  ClipboardList, 
  DollarSign, 
  Database, 
  Briefcase, 
  AlertTriangle, 
  BarChart3,
  Play,
  GitBranch
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REPORTS = [
  {
    id: 'user-activity',
    name: 'User Activity Report',
    description: 'Track all user actions in the system including logins, actions performed, and timestamps.',
    icon: Users,
    color: '#3B82F6',
    endpoint: '/reports/user-activity',
    filters: ['dateRange', 'employee']
  },
  {
    id: 'excel-upload',
    name: 'Excel Upload Report',
    description: 'Track Excel uploads that start workflow processes with file details and processing status.',
    icon: FileSpreadsheet,
    color: '#10B981',
    endpoint: '/reports/excel-upload',
    filters: ['dateRange']
  },
  {
    id: 'quote-progress',
    name: 'Quote Progress Report',
    description: 'Monitor quote completion and status with supervisor details and work progress.',
    icon: ClipboardList,
    color: '#F59E0B',
    endpoint: '/reports/quote-progress',
    filters: ['dateRange', 'quote', 'status']
  },
  {
    id: 'quote-lifecycle',
    name: 'Quote Lifecycle Report',
    description: 'Complete workflow lifecycle of a single quote - uploads, fund/material requests, approvals, billing, and all status changes with timestamps.',
    icon: GitBranch,
    color: '#7C3AED',
    endpoint: '/reports/quote-lifecycle',
    filters: ['quoteRequired'],
    requiresQuote: true
  },
  {
    id: 'detailed-quote-report',
    name: 'Detailed Quote Report',
    description: 'Comprehensive report with all quote details, fund requests, material requests, billing, AFM validation, and financial analysis. Super Admin only.',
    icon: FileSpreadsheet,
    color: '#059669',
    endpoint: '/reports/detailed-quote-report',
    filters: ['quoteDateRange', 'quoteWithAll'],
    superAdminOnly: true
  },
  {
    id: 'task-assignment',
    name: 'Task Assignment Report',
    description: 'Track tasks assigned to employees including fund and material requests.',
    icon: Briefcase,
    color: '#8B5CF6',
    endpoint: '/reports/task-assignment',
    filters: ['dateRange', 'employee']
  },
  {
    id: 'financial-audit',
    name: 'Financial Audit Report',
    description: 'Track invoices and payments for quotes with complete financial breakdown.',
    icon: DollarSign,
    color: '#DC143C',
    endpoint: '/reports/financial-audit',
    filters: ['dateRange', 'quote']
  },
  {
    id: 'data-modification',
    name: 'Data Modification Log',
    description: 'Track any changes made to system data with old and new values.',
    icon: Database,
    color: '#6366F1',
    endpoint: '/reports/data-modification',
    filters: ['dateRange', 'module']
  },
  {
    id: 'employee-workload',
    name: 'Employee Workload Report',
    description: 'Track employee task load and productivity with workload status indicators.',
    icon: Users,
    color: '#EC4899',
    endpoint: '/reports/employee-workload',
    filters: ['employee']
  },
  {
    id: 'system-errors',
    name: 'System Error Report',
    description: 'Monitor application errors and exceptions logged in the system.',
    icon: AlertTriangle,
    color: '#EF4444',
    endpoint: '/reports/system-errors',
    filters: ['dateRange']
  },
  {
    id: 'daily-summary',
    name: 'Daily Operational Summary',
    description: 'Provide daily management overview with quotes, tasks, and approvals count.',
    icon: BarChart3,
    color: '#14B8A6',
    endpoint: '/reports/daily-summary',
    filters: ['dateRange']
  }
];

export default function AuditReportsPage() {
  const { hasRole } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [generating, setGenerating] = useState({});
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [quoteNo, setQuoteNo] = useState('');
  const [quoteSearchText, setQuoteSearchText] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [employeeName, setEmployeeName] = useState('');
  const [status, setStatus] = useState('');
  const [userList, setUserList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [quoteList, setQuoteList] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(true);
  
  const isSuperAdmin = hasRole('Super Admin');
  
  // Filter quotes based on search text
  const filteredQuotes = quoteList.filter(quote => 
    quote.quote_no?.toLowerCase().includes(quoteSearchText.toLowerCase())
  ).slice(0, 10); // Show max 10 suggestions

  // Fetch users for the employee dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUserList(response.data || []);
      } catch (error) {
        console.error('Failed to fetch users:', error);
        toast.error('Failed to load user list');
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, []);

  // Fetch quotes for the quote dropdown
  useEffect(() => {
    const fetchQuotes = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/quotes`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setQuoteList(response.data || []);
      } catch (error) {
        console.error('Failed to fetch quotes:', error);
        toast.error('Failed to load quote list');
      } finally {
        setLoadingQuotes(false);
      }
    };
    fetchQuotes();
  }, []);

  const openReportDialog = (report) => {
    setSelectedReport(report);
    setStartDate('');
    setEndDate('');
    setQuoteNo('');
    setQuoteSearchText('');
    setShowSuggestions(false);
    setEmployeeName('');
    setStatus('');
    setDialogOpen(true);
  };

  const generateAndDownload = async (report) => {
    // Determine which quote number to use (for Super Admin, use search text; for others, use dropdown selection)
    const effectiveQuoteNo = isSuperAdmin ? quoteSearchText.trim() : quoteNo;
    
    // For quote-lifecycle report, quote selection is mandatory
    if (report.requiresQuote && (!effectiveQuoteNo || effectiveQuoteNo === 'all')) {
      toast.error('Please enter or select a specific quote number to generate this report');
      return;
    }
    
    setGenerating(prev => ({ ...prev, [report.id]: true }));
    
    try {
      // Build query params
      const params = new URLSearchParams();
      
      // Handle different date range filter types
      if (report.filters.includes('quoteDateRange')) {
        // Use start_date and end_date for quote date filtering
        if (startDate) params.append('start_date', startDate);
        if (endDate) params.append('end_date', endDate);
      } else if (startDate || endDate) {
        if (startDate) params.append('start_date', startDate);
        if (endDate) params.append('end_date', endDate);
      }
      
      // For quoteRequired filter, always append quote_no (no 'all' option)
      if (report.filters.includes('quoteRequired')) {
        params.append('quote_no', effectiveQuoteNo);
      } else if (report.filters.includes('quoteWithAll')) {
        // For quoteWithAll, allow "all" or specific quote
        if (effectiveQuoteNo && effectiveQuoteNo !== '') {
          params.append('quote_no', effectiveQuoteNo || 'all');
        } else {
          params.append('quote_no', 'all');
        }
      } else if (effectiveQuoteNo && effectiveQuoteNo !== 'all' && report.filters.includes('quote')) {
        params.append('quote_no', effectiveQuoteNo);
      }
      
      if (employeeName && employeeName !== 'all' && report.filters.includes('employee')) params.append('employee_name', employeeName);
      if (status && report.filters.includes('status')) params.append('status', status);
      
      const response = await axios.get(`${API}${report.endpoint}?${params.toString()}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename in ReportName_DD_MM_YYYY format
      const today = new Date();
      const dd = String(today.getDate()).padStart(2, '0');
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const yyyy = today.getFullYear();
      
      // For quote-lifecycle, include the quote number in filename
      let filename;
      if (report.id === 'quote-lifecycle' && effectiveQuoteNo) {
        const safeQuoteNo = effectiveQuoteNo.replace(/\//g, '_');
        filename = `QuoteLifecycle_${safeQuoteNo}_${dd}_${mm}_${yyyy}.xlsx`;
      } else {
        const reportNameFormatted = report.name.replace(/\s+/g, '');
        filename = `${reportNameFormatted}_${dd}_${mm}_${yyyy}.xlsx`;
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${report.name} downloaded successfully`);
      setDialogOpen(false);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('No quotes found matching the criteria.');
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.detail || 'Too many records. Please narrow your date range.');
      } else {
        toast.error(`Failed to generate ${report.name}`);
      }
      console.error(error);
    } finally {
      setGenerating(prev => ({ ...prev, [report.id]: false }));
    }
  };

  return (
    <Sidebar>
      <div className="space-y-6" data-testid="audit-reports-page">
        <div>
          <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Audit Reports
          </h1>
          <p className="text-muted-foreground mt-2">
            Generate and download system audit reports in Excel format
          </p>
        </div>

        {/* Reports Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {REPORTS.map((report) => {
            const Icon = report.icon;
            return (
              <Card 
                key={report.id} 
                className="p-6 glass-card hover:scale-[1.02] transition-all duration-300"
                data-testid={`report-card-${report.id}`}
              >
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div 
                      className="p-3 rounded-xl"
                      style={{ backgroundColor: `${report.color}20` }}
                    >
                      <Icon className="h-6 w-6" style={{ color: report.color }} />
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {report.name}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                      {report.description}
                    </p>
                  </div>
                  
                  <Button
                    size="sm"
                    onClick={() => openReportDialog(report)}
                    className="w-full"
                    style={{ backgroundColor: report.color }}
                    data-testid={`generate-btn-${report.id}`}
                  >
                    <Play className="h-3 w-3 mr-1" />
                    Generate Report
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Report Filter Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selectedReport && (
                <>
                  <div 
                    className="p-2 rounded-lg"
                    style={{ backgroundColor: `${selectedReport.color}20` }}
                  >
                    {React.createElement(selectedReport.icon, { 
                      className: "h-5 w-5", 
                      style: { color: selectedReport.color } 
                    })}
                  </div>
                  {selectedReport.name}
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedReport && (
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">
                {selectedReport.description}
              </p>
              
              <div className="p-4 rounded-xl bg-gradient-to-br from-white/60 to-white/30 backdrop-blur-md border border-white/40 space-y-4">
                <h4 className="font-semibold text-sm">Filters</h4>
                
                {selectedReport.filters.includes('dateRange') && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="dialog_start_date">Start Date</Label>
                      <Input
                        id="dialog_start_date"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="dialog_end_date">End Date</Label>
                      <Input
                        id="dialog_end_date"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </div>
                  </div>
                )}
                
                {selectedReport.filters.includes('quote') && (
                  <div className="space-y-2">
                    <Label htmlFor="quote_filter">Quote No</Label>
                    <Select
                      value={quoteNo}
                      onValueChange={(value) => setQuoteNo(value)}
                    >
                      <SelectTrigger id="quote_filter" data-testid="quote-filter-dropdown">
                        <SelectValue placeholder={loadingQuotes ? "Loading quotes..." : "Select a quote"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Quotes</SelectItem>
                        {quoteList.map((quote) => (
                          <SelectItem key={quote.id} value={quote.quote_no}>
                            {quote.quote_no}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {selectedReport.filters.includes('quoteRequired') && (
                  <div className="space-y-2">
                    <Label htmlFor="quote_filter_required" className="flex items-center gap-1">
                      Quote No <span className="text-red-500">*</span>
                    </Label>
                    
                    {/* Super Admin: Autocomplete text input with suggestions */}
                    {isSuperAdmin ? (
                      <div className="relative">
                        <Input
                          id="quote_search_input"
                          type="text"
                          placeholder="Type quote number (e.g., 00123 or 01963/26-27)"
                          value={quoteSearchText}
                          onChange={(e) => {
                            setQuoteSearchText(e.target.value);
                            setShowSuggestions(e.target.value.length > 0);
                          }}
                          onFocus={() => quoteSearchText.length > 0 && setShowSuggestions(true)}
                          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                          data-testid="quote-autocomplete-input"
                          autoComplete="off"
                        />
                        
                        {/* Suggestions dropdown */}
                        {showSuggestions && filteredQuotes.length > 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                            {filteredQuotes.map((quote) => (
                              <div
                                key={quote.id}
                                className="px-3 py-2 hover:bg-purple-50 cursor-pointer text-sm border-b border-gray-100 last:border-b-0"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  setQuoteSearchText(quote.quote_no);
                                  setShowSuggestions(false);
                                }}
                              >
                                {quote.quote_no}
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {showSuggestions && quoteSearchText.length > 0 && filteredQuotes.length === 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg p-3 text-sm text-gray-500">
                            No matching quotes found. You can still enter the exact quote number.
                          </div>
                        )}
                      </div>
                    ) : (
                      <Select
                        value={quoteNo}
                        onValueChange={(value) => setQuoteNo(value)}
                      >
                        <SelectTrigger id="quote_filter_required" data-testid="quote-filter-required-dropdown">
                          <SelectValue placeholder={loadingQuotes ? "Loading quotes..." : "Select a quote (required)"} />
                        </SelectTrigger>
                        <SelectContent>
                          {quoteList.map((quote) => (
                            <SelectItem key={quote.id} value={quote.quote_no}>
                              {quote.quote_no}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    
                    <p className="text-xs text-amber-600">
                      {isSuperAdmin 
                        ? "Start typing to see matching quotes, or enter the full quote number."
                        : "This report requires selecting a specific quote number."}
                    </p>
                  </div>
                )}
                
                {/* Quote Date Range Filter for Detailed Quote Report */}
                {selectedReport.filters.includes('quoteDateRange') && (
                  <div className="space-y-4 p-3 bg-gray-50 rounded-lg">
                    <Label className="text-sm font-medium">Quotation Date Range</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="start_date_quote" className="text-xs text-gray-500">Start Date</Label>
                        <Input
                          id="start_date_quote"
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          data-testid="quote-start-date"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="end_date_quote" className="text-xs text-gray-500">End Date</Label>
                        <Input
                          id="end_date_quote"
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          data-testid="quote-end-date"
                        />
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Quote Filter with "All" option for Detailed Quote Report */}
                {selectedReport.filters.includes('quoteWithAll') && (
                  <div className="space-y-2">
                    <Label htmlFor="quote_with_all" className="flex items-center gap-1">
                      Quote No
                    </Label>
                    <div className="relative">
                      <Input
                        id="quote_with_all_input"
                        type="text"
                        placeholder="Type quote number or leave empty for All"
                        value={quoteSearchText}
                        onChange={(e) => {
                          setQuoteSearchText(e.target.value);
                          setShowSuggestions(e.target.value.length > 0);
                        }}
                        onFocus={() => quoteSearchText.length > 0 && setShowSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                        data-testid="quote-with-all-input"
                        autoComplete="off"
                      />
                      
                      {/* Suggestions dropdown */}
                      {showSuggestions && filteredQuotes.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                          {filteredQuotes.map((quote) => (
                            <div
                              key={quote.id}
                              className="px-3 py-2 hover:bg-green-50 cursor-pointer text-sm border-b border-gray-100 last:border-b-0"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setQuoteSearchText(quote.quote_no);
                                setShowSuggestions(false);
                              }}
                            >
                              {quote.quote_no}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      Leave empty to generate report for all quotes within the date range.
                    </p>
                  </div>
                )}
                
                {selectedReport.filters.includes('employee') && (
                  <div className="space-y-2">
                    <Label htmlFor="employee_filter">Employee Name</Label>
                    <Select
                      value={employeeName}
                      onValueChange={(value) => setEmployeeName(value)}
                    >
                      <SelectTrigger id="employee_filter" data-testid="employee-filter-dropdown">
                        <SelectValue placeholder={loadingUsers ? "Loading users..." : "Select an employee"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Employees</SelectItem>
                        {userList.map((user) => (
                          <SelectItem key={user.id} value={user.full_name || user.name || user.email}>
                            {user.full_name || user.name || user.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {selectedReport.filters.includes('status') && (
                  <div className="space-y-2">
                    <Label htmlFor="status_filter">Status</Label>
                    <select
                      id="status_filter"
                      className="w-full p-2 border rounded-md"
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                    >
                      <option value="">All Statuses</option>
                      <option value="pending">Pending</option>
                      <option value="work_in_progress">Work In Progress</option>
                      <option value="work_completed">Work Completed</option>
                      <option value="jobsheet_pending">Jobsheet Pending</option>
                      <option value="billing_approved">Completed</option>
                    </select>
                  </div>
                )}
                
                {selectedReport.filters.includes('module') && (
                  <div className="space-y-2">
                    <Label htmlFor="module_filter">Module</Label>
                    <select
                      id="module_filter"
                      className="w-full p-2 border rounded-md"
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                    >
                      <option value="">All Modules</option>
                      <option value="quote">Quotes</option>
                      <option value="fund_request">Fund Requests</option>
                      <option value="material_request">Material Requests</option>
                      <option value="user">Users</option>
                    </select>
                  </div>
                )}
              </div>
              
              <div className="flex gap-2">
                <Button
                  onClick={() => generateAndDownload(selectedReport)}
                  disabled={generating[selectedReport.id]}
                  className="flex-1"
                  style={{ backgroundColor: selectedReport.color }}
                >
                  {generating[selectedReport.id] ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Generate & Download Excel
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Sidebar>
  );
}
